import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useToast } from './ToastContext';
import { API_BASE_URL as API_URL } from '../config/apiBase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const { showSuccess, showError, showInfo } = useToast();
  
  // Check if token is valid and not expired
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  // Get user info from token
  const getUserFromToken = (token) => {
    if (!token) return null;
    
    try {
      const decoded = jwtDecode(token);
      return {
        id: decoded.id,
        username: decoded.username,
        email: decoded.email,
        role: decoded.role,
        region: decoded.region,
        customerId: decoded.customerId || 0,
        mobileNumber: decoded.mobileNumber || null,
        location: decoded.location || null,
        sectorId: decoded.sectorId || null,
        dashboardHeader: decoded.dashboardHeader || null,
        entityId: decoded.entityId || null
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      if (token && isTokenValid(token)) {
        const userInfo = getUserFromToken(token);
        setUser(userInfo);
        
        // Verify token with server
        try {
          const response = await fetch(`${API_URL}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (!response.ok) {
            // Only logout if it's a 401 (unauthorized) or 403 (forbidden)
            if (response.status === 401 || response.status === 403) {
              const errorData = await response.json().catch(() => ({}));
              const errorMessage = errorData.message || 'Authentication failed';
              
              // If account is deactivated, show specific message
              if (errorMessage.includes('deactivated')) {
                showError(errorMessage);
              }
              
              logout();
            } else {
              // For other errors (500, 404, etc.), keep the user logged in
              // as the token might still be valid
              console.warn('Server error during token validation, keeping user logged in');
            }
          } else {
            const data = await response.json();
            if (data.success && data.data && data.data.user) {
              const apiUser = data.data.user;
              setUser({
                ...apiUser,
                sectorId: apiUser.sectorId ?? apiUser.sector_id ?? null,
                entityId: apiUser.entityId ?? apiUser.entity_id ?? null,
                dashboardHeader: apiUser.dashboardHeader ?? apiUser.dashboard_header ?? null,
                productTier: apiUser.productTier ?? apiUser.product_tier ?? 'B'
              });
            }
          }
        } catch (error) {
          // Network errors shouldn't cause logout - keep user logged in
          console.warn('Network error during token validation, keeping user logged in:', error);
        }
      } else if (token) {
        // Token is invalid, remove it
        console.log('Invalid token found, logging out');
        logout();
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, [token]);

  // Login function - handles both regular and Google OAuth login
  const login = async (username, email, password, token = null, userInfo = null) => {
    try {
      // If token and userInfo are provided (Google OAuth), use them directly
      if (token && userInfo) {
        setToken(token);
        setUser(userInfo);
        return { success: true, user: userInfo };
      }

      // Regular login flow
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle deactivated account specifically
        if (response.status === 403 && data.message && data.message.includes('deactivated')) {
          throw new Error(data.message);
        }
        throw new Error(data.message || 'Login failed');
      }

      const { token: newToken, user: newUserInfo } = data.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser({
        ...newUserInfo,
        sectorId: newUserInfo.sectorId ?? newUserInfo.sector_id ?? null,
        entityId: newUserInfo.entityId ?? newUserInfo.entity_id ?? null,
        dashboardHeader: newUserInfo.dashboardHeader ?? newUserInfo.dashboard_header ?? null,
        productTier: newUserInfo.productTier ?? newUserInfo.product_tier ?? 'B'
      });
      
      return { success: true, user: newUserInfo };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Register function
  const register = async (username, fullName, email, password, sectorId) => {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, fullName, email, password, sectorId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Registration failed');
      }

      const { token: newToken, user: userInfo } = data.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser({
        ...userInfo,
        sectorId: userInfo.sectorId ?? userInfo.sector_id ?? null,
        entityId: userInfo.entityId ?? userInfo.entity_id ?? null,
        dashboardHeader: userInfo.dashboardHeader ?? userInfo.dashboard_header ?? null,
        productTier: userInfo.productTier ?? userInfo.product_tier ?? 'B'
      });
      
      return { success: true, user: userInfo };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    showInfo('Logged out successfully', 1500);
    setToken(null);
    setUser(null);
  };

  // Get auth headers for API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Refresh user from server
  const refreshUser = async () => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return null;
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (data?.success && data?.data?.user) {
        const apiUser = data.data.user;
        const normalized = {
          ...apiUser,
          sectorId: apiUser.sectorId ?? apiUser.sector_id ?? null,
          entityId: apiUser.entityId ?? apiUser.entity_id ?? null,
          dashboardHeader: apiUser.dashboardHeader ?? apiUser.dashboard_header ?? null,
          productTier: apiUser.productTier ?? apiUser.product_tier ?? 'B'
        };
        setUser(normalized);
        return normalized;
      }
      return null;
    } catch (_) {
      return null;
    }
  };

  // Locally set sectorId after onboarding
  const setSectorId = (sectorId) => {
    setUser((prev) => (prev ? { ...prev, sectorId } : prev));
  };

  // Locally set entityId when associated/updated
  const setEntityId = (entityId) => {
    setUser((prev) => (prev ? { ...prev, entityId } : prev));
  };

  // Update dashboard header
  const updateDashboardHeader = async (dashboardHeader) => {
    const currentToken = localStorage.getItem('token');
    if (!currentToken) return { success: false, error: 'No token found' };
    
    try {
      const response = await fetch(`${API_URL}/auth/dashboard-header`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dashboardHeader })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update dashboard header');
      }

      if (data.success && data.data && data.data.user) {
        const apiUser = data.data.user;
        const updatedUser = {
          ...apiUser,
          sectorId: apiUser.sectorId ?? apiUser.sector_id ?? null,
          entityId: apiUser.entityId ?? apiUser.entity_id ?? null,
          dashboardHeader: apiUser.dashboardHeader ?? apiUser.dashboard_header ?? null,
          productTier: apiUser.productTier ?? apiUser.product_tier ?? 'B'
        };
        setUser(updatedUser);
        return { success: true, user: updatedUser };
      }

      return { success: false, error: 'Invalid response from server' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    getAuthHeaders,
    isAuthenticated: !!user,
    refreshUser,
    setSectorId,
    updateDashboardHeader,
    setEntityId
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
